import React, { ChangeEvent, Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { delay } from "@formant/core/common/delay";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IGoogleStorageInfo } from "@formant/core/model/IGoogleStorageInfo";
import { IValidation } from "@formant/core/model/IValidation";
import { Uuid } from "@formant/core/model/Uuid";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";
import { animate } from "@formant/ui/utils/animate";

import { organizationStore } from "../../../stores";
import { Buttons } from "../../common/Buttons";
import { ContentTabs, IContentTab } from "../../common/ContentTabs";
import { Heading3 } from "../../common/Heading3";
import { IconButton } from "../../common/IconButton";
import { StandardButton } from "../../common/StandardButton";
import { ValidationMessages } from "../../common/ValidationMessages";
import * as styles from "./EditGcsInfo.scss";

export interface IEditGcsInfoProps {
    organizationId: Uuid;
    gcsInfo: IGoogleStorageInfo;
    onClose: () => void;
}

@observer
export class EditGcsInfo extends Component<IEditGcsInfoProps> {
    @observable
    private gcsInfo: IGoogleStorageInfo = this.props.gcsInfo;
    @observable
    private hidden: boolean = true;
    @observable
    private validation?: IValidation;

    public async componentDidMount() {
        await delay();
        this.hidden = false;
    }

    public render() {
        const { googleStorageExport } = this.gcsInfo;

        const tabs: IContentTab[] = [
            {
                name: "Configure integration",
                content: (
                    <div>
                        <ul>
                            <li>
                                <p>Service Account JSON Key</p>
                                <TextField
                                    className={styles.field}
                                    label="JSON Key"
                                    small={true}
                                    value={this.gcsInfo.credentialsJsonKey}
                                    invalid={
                                        !!this.validation?.["awsInfo.accountId"]
                                    }
                                    onChange={this.onChangeJsonKey}
                                />
                            </li>
                            <li>
                                <p>
                                    The name of your Google Cloud Storage
                                    bucket.
                                </p>
                                <TextField
                                    className={styles.field}
                                    label="Bucket"
                                    small={true}
                                    value={googleStorageExport?.bucket || ""}
                                    invalid={
                                        !!this.validation?.[
                                            "awsInfo.s3Export.bucket"
                                        ]
                                    }
                                    onChange={this.onChangeBucket}
                                />
                            </li>
                            <li>
                                <p>
                                    The path to export to within your Google
                                    Cloud Storage bucket (optional)
                                </p>
                                <TextField
                                    className={styles.field}
                                    label="Prefix"
                                    small={true}
                                    value={googleStorageExport?.path || ""}
                                    invalid={
                                        !!this.validation?.[
                                            "awsInfo.s3Export.path"
                                        ]
                                    }
                                    onChange={this.onChangePath}
                                />
                            </li>
                        </ul>
                    </div>
                ),
            },
            {
                name: "Create a Service Account JSON Key",
                content: (
                    <div>
                        <ol className={styles["numbered-list"]}>
                            <li>
                                To allow access to your bucket you must create a
                                service account and configure a JSON key token
                                that will let our servers upload data. Follow
                                these steps below:
                            </li>
                            <li>
                                <div className={styles.terminal}>
                                    gcloud login
                                    <br />
                                    gcloud config set project PROJECT_ID
                                    <br />
                                    gsutil mb gs://BUCKET_NAME
                                    <br />
                                    gcloud iam service-accounts create
                                    SERVICE_ACCOUNT_ID
                                    <br />
                                    gsutil iam ch
                                    serviceAccount:SERVICE_ACCOUNT_ID@PROJECT_ID.iam.gserviceaccount.com:objectAdmin
                                    gs://BUCKET_NAME
                                    <br />
                                    gcloud iam service-accounts keys create
                                    keepsecret.json --iam-account
                                    SERVICE_ACCOUNT_ID@PROJECT_ID.iam.gserviceaccount.com
                                    <br />
                                </div>
                            </li>
                            <li>
                                Once complete open the JSON file and copy its
                                contents and paste it to the "Service Account
                                JSON Key" field.
                            </li>
                        </ol>
                    </div>
                ),
            },
        ];

        return (
            <div
                className={classnames(
                    styles["edit-gcs-info"],
                    this.hidden && styles.hidden,
                )}
            >
                <div className={styles.header}>
                    <Heading3 className={styles.heading}>
                        Configure Google Cloud Storage Integration
                    </Heading3>
                    <IconButton
                        icon="close"
                        className={styles.close}
                        onClicked={this.onClose}
                    />
                </div>

                <ContentTabs tabs={tabs} />
                <ValidationMessages validation={this.validation} />
                <Buttons className={styles.buttons}>
                    <StandardButton primary={true} onClicked={this.onSave}>
                        Save and close
                    </StandardButton>
                </Buttons>
            </div>
        );
    }

    private onChangeJsonKey = (event: ChangeEvent<InputElement>) => {
        this.gcsInfo.credentialsJsonKey = event.target.value;
    };

    private onChangeBucket = (event: ChangeEvent<InputElement>) => {
        this.gcsInfo.googleStorageExport = {
            ...(this.gcsInfo.googleStorageExport || {}),
            bucket: event.target.value,
        };
    };

    private onChangePath = (event: ChangeEvent<InputElement>) => {
        this.gcsInfo.googleStorageExport = {
            bucket: "",
            ...(this.gcsInfo.googleStorageExport || {}),
            path: event.target.value,
        };
    };

    private onClose = async () => {
        const { onClose } = this.props;
        this.hidden = true;
        await animate();
        onClose();
    };

    private onSave = async () => {
        const { gcsInfo } = this;
        const { organizationId, onClose } = this.props;
        try {
            await organizationStore.update(organizationId, {
                googleStorageInfo: gcsInfo,
            });
            this.hidden = true;
            await animate();
            onClose();
        } catch (error) {
            const responseError = narrowError(error, ResponseError);
            if (responseError.statusCode === 422) {
                this.validation = responseError.validationErrors;
            } else {
                throw error;
            }
        }
    };
}
